// 攻略実績：みんなの戦いの記録を集計して、攻略wiki（honulabs.net/hyakki/wiki/records/）に出す。
// サーバー（worker の wiki-stats.ts）が D1 から人ごとにまとめた行を取り、ここで戦場・キャラごとの数字にする。
// 出すのは人数・割合・中央値などの集計だけ（個人の編成は出さない）。名前を出すのは最少手番の1位だけで、番付と同じく公開済みの表示名。
import { defaultJutsu } from '../data/jutsu';
import { CHAR_IDS, type BattleLoadout, type CharId } from '../types';
import { omamoriKind, equippedOmamori } from './omamori';
import type { Profile } from './profile';
import { lineLevels } from './skill-lines';
import { equippedSkills } from './skills';

/** 戦いの時点の構成の要約（battle_sessions.build に入れる。数文字の JSON） */
export interface BuildSummary {
  /** 持っていった術（無ければキャラの基本の術） */
  j: string | null;
  /** スキルの系統 Lv（SKILL_LINES の順） */
  l: number[];
  /** お守り（種類。sameAs はまとめる） */
  o: string | null;
  /** 式神召喚（敵ID） */
  s: string | null;
  /** おすすめ構成に任せているか */
  a: 0 | 1;
}

export function buildSummary(p: Profile, charId: CharId, lo: BattleLoadout): BuildSummary {
  const om = equippedOmamori(p, charId)[0];
  return {
    j: lo.jutsu ?? defaultJutsu(charId).id,
    l: lineLevels(charId, equippedSkills(p, charId)),
    o: om ? omamoriKind(om.defId) : null,
    s: lo.companion?.enemyId ?? null,
    a: p.skills[charId]?.auto ? 1 : 0,
  };
}

/** 人×戦場×難易度×キャラでまとめた行（SQL の GROUP BY の結果） */
export interface RecordRow {
  u: string;
  s: number;
  d: number;
  c: string;
  /** 戦った数・勝った数 */
  n: number;
  w: number;
  /** 勝った戦いのうち最も低い戦力（記録が無い古い戦いは null） */
  p: number | null;
  /** 勝った戦いの最少手番 */
  t: number | null;
}

/** 人×キャラの最後に勝った戦いの構成 */
export interface BuildRow {
  u: string;
  c: string;
  build: BuildSummary;
}

/** 最少手番の上位（戦場・難易度ごと。戦力の上限で外したあとの1位を使う） */
export interface BestRow {
  s: number;
  d: number;
  name: string;
  turns: number;
  power: number;
  c: string;
}

export interface StageRecord {
  s: number;
  d: number;
  /** 挑んだ人・勝った人 */
  players: number;
  cleared: number;
  /** 勝てた戦力（人ごとの最低。下位10%と中央値） */
  power: { p10: number; p50: number } | null;
  /** 勝った手番の中央値（人ごとの最少） */
  turns: number | null;
  /** 勝った人のキャラ（人数） */
  chars: Partial<Record<CharId, number>>;
  best: { name: string; turns: number; c: string } | null;
}

export interface CharBuildRecord {
  /** 集計した人数 */
  n: number;
  jutsu: Record<string, number>;
  /** 系統の Lv の平均 */
  lines: number[];
  omamori: Record<string, number>;
  companion: Record<string, number>;
  /** おすすめ構成に任せている人 */
  auto: number;
}

export interface WikiStats {
  v: 1;
  at: number;
  days: number;
  minPlayers: number;
  totals: { battles: number; players: number };
  /** キャラごとに勝った人数（期間中に一度でもそのキャラで勝った人） */
  chars: Partial<Record<CharId, number>>;
  stages: StageRecord[];
  builds: Partial<Record<CharId, CharBuildRecord>>;
}

/** 少なすぎる人数の戦場・キャラは出さない（個人が分かってしまう・数字が揺れる） */
export const RECORDS_MIN_PLAYERS = 5;
export const RECORDS_DAYS = 30;

/** 小さい方から q（0..1）の位置の値（最も近い順位） */
export function percentile(xs: number[], q: number): number {
  const a = [...xs].sort((x, y) => x - y);
  return a[Math.min(a.length - 1, Math.max(0, Math.ceil(q * a.length) - 1))];
}

const inc = <K extends string>(m: Partial<Record<K, number>>, k: K) => ((m[k] = (m[k] ?? 0) + 1), m);

export function summarizeRecords(input: {
  rows: RecordRow[];
  builds: BuildRow[];
  best: BestRow[];
  battles: number;
  now: number;
  days?: number;
  minPlayers?: number;
}): WikiStats {
  const min = input.minPlayers ?? RECORDS_MIN_PLAYERS;
  // 戦場×難易度 → 人ごと（キャラを分けた行を人にまとめる）
  type Person = { n: number; w: number; p: number | null; t: number | null; c: string | null; cw: number };
  const byStage = new Map<string, Map<string, Person>>();
  const players = new Set<string>();
  const charUsers: Partial<Record<CharId, Set<string>>> = {};
  for (const r of input.rows) {
    players.add(r.u);
    const key = `${r.s}:${r.d}`;
    const m = byStage.get(key) ?? new Map<string, Person>();
    byStage.set(key, m);
    const x = m.get(r.u) ?? { n: 0, w: 0, p: null, t: null, c: null, cw: 0 };
    x.n += r.n;
    x.w += r.w;
    if (r.w > 0) {
      if (r.p != null && (x.p == null || r.p < x.p)) x.p = r.p;
      if (r.t != null && (x.t == null || r.t < x.t)) x.t = r.t;
      // その戦場でいちばん多く勝ったキャラを、その人のキャラとする
      if (r.w > x.cw) (x.c = r.c), (x.cw = r.w);
      if ((CHAR_IDS as string[]).includes(r.c)) (charUsers[r.c as CharId] ??= new Set()).add(r.u);
    }
    m.set(r.u, x);
  }
  const best = new Map(input.best.map((b) => [`${b.s}:${b.d}`, b]));
  const stages: StageRecord[] = [];
  for (const [key, m] of byStage) {
    const [s, d] = key.split(':').map(Number);
    const people = [...m.values()];
    if (people.length < min) continue;
    const won = people.filter((x) => x.w > 0);
    const powers = won.map((x) => x.p).filter((x): x is number => x != null && x > 0);
    const turns = won.map((x) => x.t).filter((x): x is number => x != null && x > 0);
    const chars: Partial<Record<CharId, number>> = {};
    for (const x of won) if (x.c && (CHAR_IDS as string[]).includes(x.c)) inc(chars, x.c as CharId);
    const b = best.get(key);
    stages.push({
      s,
      d,
      players: people.length,
      cleared: won.length,
      power: powers.length >= min ? { p10: percentile(powers, 0.1), p50: percentile(powers, 0.5) } : null,
      turns: turns.length >= min ? percentile(turns, 0.5) : null,
      chars,
      best: b ? { name: b.name, turns: b.turns, c: b.c } : null,
    });
  }
  stages.sort((a, b) => a.d - b.d || a.s - b.s);

  const builds: Partial<Record<CharId, CharBuildRecord>> = {};
  for (const c of CHAR_IDS) {
    const rows = input.builds.filter((b) => b.c === c && b.build);
    if (rows.length < min) continue;
    const L = Math.max(0, ...rows.map((r) => r.build.l?.length ?? 0));
    const sum = Array.from({ length: L }, () => 0);
    const rec: CharBuildRecord = { n: rows.length, jutsu: {}, lines: [], omamori: {}, companion: {}, auto: 0 };
    for (const { build: b } of rows) {
      if (b.j) inc(rec.jutsu, b.j);
      if (b.o) inc(rec.omamori, b.o);
      if (b.s) inc(rec.companion, b.s);
      rec.auto += b.a ? 1 : 0;
      (b.l ?? []).forEach((v, i) => (sum[i] += v));
    }
    rec.lines = sum.map((v) => Math.round((v / rows.length) * 10) / 10);
    builds[c] = rec;
  }

  return {
    v: 1,
    at: input.now,
    days: input.days ?? RECORDS_DAYS,
    minPlayers: min,
    totals: { battles: input.battles, players: players.size },
    chars: Object.fromEntries(Object.entries(charUsers).map(([k, v]) => [k, v!.size])),
    stages,
    builds,
  };
}
